import { useState, type FormEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthProvider.tsx'
import { PilihTema } from '../components/PilihTema.tsx'

/**
 * Pesan per kode kontrak, bukan status HTTP.
 *
 * ACCOUNT_LOCKED dan UNAUTHENTICATED menuntut tindakan berbeda dari pengguna;
 * status 429 dan 401 saja tidak menyampaikan itu. Lihat ADR-0003.
 */
function messageFor(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Tidak dapat menghubungi server. Periksa jaringan lalu coba lagi.'
  }
  switch (error.code) {
    case 'UNAUTHENTICATED':
      return 'Nama pengguna atau kata sandi salah.'
    case 'ACCOUNT_LOCKED':
      return error.message // memuat durasi kunci dari server
    case 'FORBIDDEN':
      return 'Akun Anda tidak aktif. Hubungi panitia pusat.'
    case 'VALIDATION_ERROR':
      return 'Nama pengguna dan kata sandi wajib diisi.'
    default:
      return 'Terjadi gangguan pada server. Coba beberapa saat lagi.'
  }
}

export function LoginPage(): ReactNode {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await login(username, password)
      navigate('/', { replace: true })
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="login-wrap">
      <form className="login-card" onSubmit={onSubmit}>
        <span className="tag">CBT/CBS-OSCE Nasional</span>
        <h1>Masuk Panel Panitia</h1>

        <label htmlFor="username">Nama pengguna</label>
        <input
          id="username"
          name="username"
          autoComplete="username"
          autoFocus
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          disabled={busy}
        />

        <label htmlFor="password">Kata sandi</label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
        />

        {error !== null && (
          <p role="alert" className="error">
            {error}
          </p>
        )}

        <button type="submit" disabled={busy}>
          {busy ? 'Memeriksa…' : 'Masuk'}
        </button>

        <p className="hint">Lingkungan pengembangan — Wave 1. Tidak ada data ujian.</p>
      </form>

      {/* Juga di sini: layar masuk adalah satu-satunya halaman yang dilihat
          pengguna yang belum berhasil masuk, dan tema tidak menuntut sesi. */}
      <div className="login-tema">
        <PilihTema />
      </div>
    </main>
  )
}
